import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useSelector, useDispatch } from 'react-redux';
import { switchProfile, setActiveAiMember, setActiveSeller, loadAiMembers, loadSellers } from '../redux/actions/user';
import { CheckCircleIcon, CpuChipIcon, UserCircleIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { Link, useNavigate } from "react-router-dom"; 
import { XMarkIcon } from '@heroicons/react/20/solid';
import Navbar from './Layout/Navbar';
import { setStoredCurrentProfile } from '../utils/shopProfile';
import { buildAiMemberProfileKey, isAiMemberProfile } from '../utils/shopProfile';

const ProfileSwitchButton = ({ className = '' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate(); 
  const [openModal, setOpenModal] = useState(false);
  const currentProfile = useSelector((state) => state.user.currentProfile);
  const userProfile = useSelector((state) => state.user.user);
  const userLoading = useSelector((state) => state.user.loading);
  const sellerProfiles = useSelector((state) => state.seller.sellers);
  const activeSellerProfile = useSelector((state) => state.seller.activeSeller);
  const sellerProfile = useSelector((state) => state.seller.seller);
  const aiMembers = useSelector((state) => state.user.aiMembers || []);
  const activeAiMember = useSelector((state) => state.user.activeAiMember);

  useEffect(() => {
    if (userProfile && sellerProfiles.length === 0) {
      dispatch(loadSellers());
      dispatch(loadAiMembers());
    }
  }, [dispatch, userProfile, sellerProfiles.length]);

  useEffect(() => {
    console.log('Current Profile has changed to:', currentProfile);
  }, [currentProfile]);

  const currentShopProfile =
    sellerProfiles.find(seller => seller.name === currentProfile) ||
    (activeSellerProfile?.name === currentProfile ? activeSellerProfile : null) ||
    (sellerProfile?.name === currentProfile ? sellerProfile : null);

  const selectedAiMember = activeAiMember || aiMembers.find(
    (aiMember) => buildAiMemberProfileKey(aiMember) === currentProfile
  );
  const currentProfileAvatarUrl = currentProfile === 'User'
    ? userProfile?.avatar?.url
    : isAiMemberProfile(currentProfile)
      ? selectedAiMember?.avatar?.url
      : currentShopProfile?.avatar?.url;

  const handleSwitchProfile = (index) => {
    if (index === undefined) {
      dispatch(switchProfile('User'));
      setStoredCurrentProfile('User');
      navigate('/my-profile');
    } else {
      const selectedSellerProfile = sellerProfiles[index];
      dispatch(setActiveSeller(selectedSellerProfile));
      setStoredCurrentProfile(selectedSellerProfile.name);
      navigate(`/shop/preview/${selectedSellerProfile.handle}`);
    }
    setOpenModal(false);
  };

  const handleSwitchAiMember = (aiMember) => {
    dispatch(setActiveAiMember(aiMember));
    navigate(`/ai/${aiMember.handle}`);
    setOpenModal(false);
  };

  const hasMultipleProfiles = userProfile && (sellerProfiles.length > 0 || aiMembers.length > 0);

  const modalContent = (
    <div className="switcheroo-modal-overlay" onClick={() => setOpenModal(false)}>
      <div className="switcheroo-modal-box" onClick={(e) => e.stopPropagation()}>
        <button
          aria-label="close"
          onClick={() => setOpenModal(false)}
          className="switcheroo-close-icon"
        >
          <XMarkIcon aria-hidden="true" />
        </button>

        <h2 id="modal-modal-title">Switch Profile</h2>

        {sellerProfiles.map((seller, index) => (
          <div key={index} className="switcheroo-modal-item" onClick={() => handleSwitchProfile(index)}>
            <img src={seller.avatar.url} alt={`${seller.name} Avatar`} className="switcheroo-modal-item-image" />
            {seller.name}
            <BuildingStorefrontIcon className="switcheroo-shop-icon" />
            {currentProfile === seller.name && <CheckCircleIcon className="switcheroo-check-icon" />}
          </div>
        ))}

        {aiMembers.map((aiMember) => {
          const profileKey = buildAiMemberProfileKey(aiMember);
          return (
            <div key={profileKey} className="switcheroo-modal-item" onClick={() => handleSwitchAiMember(aiMember)}>
              {aiMember.avatar?.url ? (
                <img src={aiMember.avatar.url} alt={`${aiMember.name} Avatar`} className="switcheroo-modal-item-image" />
              ) : (
                <CpuChipIcon className="switcheroo-modal-item-image" aria-hidden="true" />
              )}
              {aiMember.name}
              <CpuChipIcon className="switcheroo-shop-icon" aria-label="AI Member" />
              {currentProfile === profileKey && <CheckCircleIcon className="switcheroo-check-icon" />}
            </div>
          );
        })}

        <div className="switcheroo-modal-item" onClick={() => handleSwitchProfile(undefined)}>
          <img src={userProfile?.avatar?.url} alt="User Avatar" className="switcheroo-modal-item-image" />
          {userProfile?.name}
          {currentProfile === 'User' && <CheckCircleIcon className="switcheroo-check-icon" />}
        </div>
        <div className="mobile-navbar-wrapper">
          <Navbar />
        </div>
      </div>
    </div>
  );

  return (
    <div className={className}>
      {userLoading !== false && !userProfile ? (
        <button type="button" className="switcheroo-avatar-button" aria-label="Loading profile" disabled>
          <UserCircleIcon className="h-12 w-12 text-gray-400" aria-hidden="true" />
        </button>
      ) : userProfile ? (
        <>
          <button onClick={() => setOpenModal(true)} className="switcheroo-avatar-button">
            {currentProfileAvatarUrl ? (
              <img
                src={currentProfileAvatarUrl}
                alt={`${currentProfile} Avatar`}
                className="switcheroo-current-profile-avatar"
                id="current-profile-avatar"
              />
            ) : (
              <UserCircleIcon className="h-12 w-12 text-gray-400" aria-hidden="true" />
            )}
            {hasMultipleProfiles && <span id="switcheroo-icon" className="switcheroo-swap-icon">↔</span>}
          </button>

          {openModal && ReactDOM.createPortal(modalContent, document.body)}
        </>
      ) : (
        <Link to="/login"> 
          <UserCircleIcon className="h-12 w-12 text-gray-400" aria-hidden="true" />
        </Link>
      )}
    </div>
  );
};

export default ProfileSwitchButton;



